"use client";
import { useCallback, useMemo, useState } from "react";
import type { ProductSize } from "./useSizes";

interface JeansSize extends ProductSize {
  width: string;
  length: string;
}

interface JeansSelection {
  width: string | null;
  length: string | null;
  selectedSize: ProductSize | null;
}

const useJeansSizes = (sizes?: ProductSize[]) => {
  const [selection, setSelection] = useState<JeansSelection>({
    width: null,
    length: null,
    selectedSize: null,
  });

  const jeansSizes = useMemo(() => {
    if (!sizes) return [];

    return sizes
      .map((size) => {
        const sizeName = size.name || size.label || "";
        if (!sizeName) return null;

        const matchX = sizeName.match(/(\d+)x(\d+)/i);
        const matchSlash = sizeName.match(/(?:W)?(\d+)\/(?:L)?(\d+)/i);

        if (matchX) {
          const [, width, length] = matchX;
          return { ...size, width, length } as JeansSize;
        } else if (matchSlash) {
          const [, width, length] = matchSlash;
          return { ...size, width, length } as JeansSize;
        }

        return null;
      })
      .filter((size): size is JeansSize => size !== null);
  }, [sizes]);

  const selectWidth = useCallback((width: string) => {
    setSelection((prev) =>
      prev.width === width ? prev : { width, length: null, selectedSize: null }
    );
  }, []);

  const selectLength = useCallback(
    (length: string) => {
      setSelection((prev) => {
        if (!prev.width) return prev;

        const matchingSize = jeansSizes.find(
          (size) => size.width === prev.width && size.length === length
        );

        return {
          ...prev,
          length,
          selectedSize: matchingSize || null,
        };
      });
    },
    [jeansSizes]
  );

  const getAvailableWidths = useCallback(() => {
    const widths = new Set<string>();
    jeansSizes.forEach((size) => {
      if (size.stock) widths.add(size.width);
    });
    return Array.from(widths).sort((a, b) => parseInt(a) - parseInt(b));
  }, [jeansSizes]);

  const getAvailableLengths = useCallback(
    (width: string) => {
      const lengths = new Set<string>();
      jeansSizes.forEach((size) => {
        if (size.width === width && size.stock) lengths.add(size.length);
      });
      return Array.from(lengths).sort((a, b) => parseInt(a) - parseInt(b));
    },
    [jeansSizes]
  );

  const getSizeMatrix = useCallback(() => {
    const matrix: Record<string, Record<string, JeansSize>> = {};
    jeansSizes.forEach((size) => {
      if (!matrix[size.width]) matrix[size.width] = {};
      matrix[size.width][size.length] = size;
    });
    return matrix;
  }, [jeansSizes]);

  const isSelectionComplete = useCallback(() => {
    return !!(selection.width && selection.length && selection.selectedSize);
  }, [selection]);

  const isSelectionValid = useCallback(() => {
    return isSelectionComplete() && (selection.selectedSize?.stock || false);
  }, [selection, isSelectionComplete]);

  return {
    sizes: undefined,
    selectedSize: selection.selectedSize,
    jeansSizes,
    selection,
    selectWidth,
    selectLength,
    getAvailableWidths,
    getAvailableLengths,
    getSizeMatrix,
    isSelectionComplete,
    isSelectionValid,
    getSelectedSizeForCart: () =>
      isSelectionComplete() ? selection.selectedSize : undefined,
  };
};

export default useJeansSizes;
